import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { CTASection } from '../components/sections/CTASection';
import axios from 'axios';
import { 
  MapPin, 
  GraduationCap,
  Users,
  CheckCircle2,
  ArrowRight,
  Building2,
  Calendar
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Schools = () => {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSchools = async () => {
      try {
        const response = await axios.get(`${API}/schools`);
        setSchools(response.data);
      } catch (error) {
        console.error('Error fetching schools:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSchools();
  }, []);

  const regions = [
    { name: "Virginia", states: ["VA"] },
    { name: "New England", states: ["VT", "NH", "MA"] },
    { name: "Midwest", states: ["IN", "OH"] },
    { name: "Southeast", states: ["GA", "AL", "NY", "MD"] }
  ];

  return (
    <div data-testid="schools-page">
      {/* Hero Section - White */}
      <section className="bg-white section-padding border-b border-gray-100" data-testid="schools-hero">
        <div className="section-container">
          <motion.div 
            className="max-w-3xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="text-sm font-bold uppercase tracking-widest text-red-600 mb-4 block">
              Campus Network
            </span>
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-black text-navy mb-6 leading-tight"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              Schools We Serve
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Student Store It currently serves students at top universities across the country. 
              Select your school to view pickup dates and dorm locations.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Universities Grid */}
      <section className="section-padding bg-gray-50" data-testid="universities-grid">
        <div className="section-container">
          {loading ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 border-4 border-navy border-t-transparent rounded-full animate-spin mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading schools...</p>
            </div>
          ) : (
            <>
              <motion.div 
                className="text-center mb-12"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <h2 
                  className="text-3xl md:text-4xl font-bold text-navy mb-4"
                  style={{ fontFamily: 'Chivo, sans-serif' }}
                >
                  Our Partner Universities
                </h2>
                <p className="text-gray-600 max-w-2xl mx-auto">
                  We're building campus operations and student support around these schools to ensure 
                  a smooth experience every semester.
                </p>
              </motion.div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {schools.map((school, index) => (
                  <motion.div
                    key={school.id}
                    className="bg-white border border-gray-200 rounded-xl p-6 hover:border-navy/20 hover:shadow-lg transition-all"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                    data-testid={`university-${school.short.toLowerCase()}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 bg-navy/5 rounded-xl flex items-center justify-center">
                        <GraduationCap className="w-6 h-6 text-navy" />
                      </div>
                      <span className="px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700">
                        Active
                      </span>
                    </div>
                    
                    <h3 
                      className="text-xl font-bold text-navy mb-1"
                      style={{ fontFamily: 'Chivo, sans-serif' }}
                    >
                      {school.name}
                    </h3>
                    <div className="flex items-center gap-2 text-gray-500 text-sm mb-3">
                      <MapPin className="w-4 h-4" />
                      {school.location}
                    </div>
                    
                    <div className="text-sm text-gray-600 mb-4">
                      <div className="flex items-center gap-2 mb-1">
                        <Calendar className="w-4 h-4 text-red-600" />
                        <span className="font-medium">Pickup:</span> 
                        {new Date(school.pickup_dates.find(d => d.type === 'standard')?.date || school.pickup_dates[0].date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {new Date(school.pickup_dates.filter(d => d.type === 'standard').pop()?.date || school.pickup_dates[school.pickup_dates.length - 1].date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-navy" />
                        <span className="font-medium">Delivery:</span>
                        {new Date(school.delivery_dates.find(d => d.type === 'standard')?.date || school.delivery_dates[0].date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {new Date(school.delivery_dates.filter(d => d.type === 'standard').pop()?.date || school.delivery_dates[school.delivery_dates.length - 1].date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </div>
                    </div>
                    
                    <Link to="/contact">
                      <Button 
                        className="w-full bg-red-600 hover:bg-red-700 text-white font-bold uppercase tracking-wide text-sm py-3 rounded-lg"
                        data-testid={`reserve-${school.short.toLowerCase()}-btn`}
                      >
                        Reserve at {school.short}
                      </Button>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Campus Operations */}
      <section className="section-padding bg-white" data-testid="campus-operations">
        <div className="section-container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-sm font-bold uppercase tracking-widest text-red-600 mb-4 block">
                Local Support
              </span>
              <h2 
                className="text-3xl md:text-4xl font-bold text-navy mb-6"
                style={{ fontFamily: 'Chivo, sans-serif' }}
              >
                Built for Students, Powered by Campus Teams
              </h2>
              <p className="text-gray-600 text-lg mb-6 leading-relaxed">
                Student Store It works with student representatives and campus teams to provide 
                storage services designed specifically for college life.
              </p>
              <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                Our campus teams help coordinate storage pickups, deliveries, and student support 
                to ensure a smooth experience every semester.
              </p>
              
              <div className="space-y-4">
                {[
                  "Local campus representatives who understand your school",
                  "Pickup and delivery scheduled around your campus calendar",
                  "Direct communication with your campus team",
                  "Support from students who've been there"
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    className="flex items-center gap-3"
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                  >
                    <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img 
                src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?q=80&w=1000&auto=format&fit=crop"
                alt="College students working together"
                className="rounded-2xl shadow-xl w-full h-[450px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-navy text-white rounded-xl p-6 shadow-xl">
                <Users className="w-8 h-8 text-red-400 mb-2" />
                <p className="font-bold text-lg" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  Student-Powered
                </p>
                <p className="text-gray-300 text-sm">
                  Campus teams at every university
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Don't See Your School */}
      <section className="section-padding bg-gray-50" data-testid="request-school">
        <div className="section-container">
          <motion.div 
            className="text-center max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Building2 className="w-12 h-12 text-navy mx-auto mb-4" />
            <h2 
              className="text-3xl font-bold text-navy mb-4"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              Don't See Your School?
            </h2>
            <p className="text-gray-600 mb-6">
              We're actively expanding to new campuses. Let us know if you'd like Student Store It 
              at your university, and we'll work on bringing our service to you.
            </p>
            <Link to="/contact">
              <Button className="btn-secondary" data-testid="request-school-btn">
                Request Your School
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <CTASection 
        headline="Ready to Reserve?"
        subtext="Find your school and book your summer storage today."
        variant="default"
      />
    </div>
  );
};

export default Schools;
